import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import FullScreenGallery from "./FullScreenGallery";
import { parseUltrasoundImages } from "../../patient-dashboard/services/ParseUltrasoundImages";
import {
  ProcessedUltrasoundImage,
  UltrasoundImage,
} from "../../../utility/interfaces/patientMasterDataInterface";
import styles from "./DatewiseUltrasoundGroup.module.scss";
import { useTranslation } from "react-i18next";

interface DatewiseUltrasoundGroupProps {
  imageDate: string;
  imageCount: number;
  ultrasoundImages: UltrasoundImage[];
  preferredLanguage: string;
}

const DatewiseUltrasoundGroup: React.FC<DatewiseUltrasoundGroupProps> = ({
  imageDate,
  imageCount,
  ultrasoundImages,
  preferredLanguage
}) => {
  const isMobile = useMediaQuery("(max-width: 900px)");
  const { t } = useTranslation();

  const ultrasoundImagesData: ProcessedUltrasoundImage[] = parseUltrasoundImages(
    ultrasoundImages,
    imageDate,
    preferredLanguage
  );

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const openGallery = (index: number) => {
    setStartIndex(index);
    setIsGalleryOpen(true);
  };

  if (ultrasoundImagesData?.length === 0) return null;

  const [mainImage, ...thumbs] = ultrasoundImagesData;

  return (
    <Box
      className={styles.container}
      sx={{ width: isMobile ? "100%" : "calc(50% - 1rem)" }}
    >
      <Typography variant="h4" gutterBottom>
        {mainImage?.date}
      </Typography>

      <Typography variant="body1" color="text.secondary" pb={2} gutterBottom>
        {imageCount}{" "}{imageCount > 1 ? t("images") : t("image")}
      </Typography>
      <img
        src={mainImage?.imageUrl}
        alt={mainImage?.imageName}
        className={styles.mainImage}
        onClick={() => openGallery(0)}
      />

      {isMobile && thumbs?.length > 0 && (
        <Box className={styles.thumbnailWrapper}>
          {thumbs.slice(0, 3).map((img, idx) => {
            const hiddenCount = thumbs.length - 3;
            const isLastWithExtras = idx === 2 && hiddenCount > 0;

            return (
              <Box
                key={idx}
                className={styles.thumbnailBox}
                onClick={() => openGallery(idx + 1)}
              >
                <img
                  src={img.imageUrl}
                  alt={img.imageName}
                  className={styles.thumbnailImage}
                />
                {isLastWithExtras && (
                  <Box className={styles.overlay}>
                    <Typography variant="subtitle1" color="white">
                      + {hiddenCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {isGalleryOpen && (
        <FullScreenGallery
          images={ultrasoundImagesData}
          open={isGalleryOpen}
          initialIndex={startIndex}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}
    </Box>
  );
};

export default DatewiseUltrasoundGroup;

